import { Alert } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
	AuthData,
	SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED,
} from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ENDORSEMENT_REVIEW_CHANGES_UNSAVED_CHANGES_MSG } from '../../../data/labelsAndMessages';
import {
	StyledRow,
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from '../../fastForm/data/constants';
import { dateConversion } from '../../fastForm/utils';
import FieldLabel from '../../fields/FieldLabel';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import ExposureChanges from '../ReviewChanges/ExposureChanges';
import PolicyChanges from '../ReviewChanges/PolicyChanges';
import {
	endorsementHasUnsavedChanges,
	filterForPolicySnapshot,
	getAllCharacteristicsFieldGroupLocators,
	getAllCharacteristicsFieldValues,
	getHasInputRepriceBehavior,
	getReferenceTimestampBasedOnState,
} from '../util_sharedEndorsementAndRenewal';

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
}

/**
 * The review changes page for an endorsement.
 */
const ReviewChanges = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { isDirty, dirtyFields } = useFormState();
	const { endorsementData, authData } = props;
	const timezone = authData.auth.timezone;

	const isSecondPrecisionEnabled =
		authData.auth.configProperties[
			SHOW_SECOND_PRECISION_ON_ENDORSEMENT_DATES_ENABLED
		];
	const DATE_PICKER_PRECISION = isSecondPrecisionEnabled
		? DATE_PRECISION.SECOND
		: DATE_PRECISION.DAY;

	const { productConfig, endorsementUpdateObj, policy, endorsementResponse } =
		endorsementData;

	if (!endorsementUpdateObj) {
		return <LargeCenterSpinner />;
	}

	const { startTimestamp, newPolicyEndTimestamp, newPaymentScheduleName } =
		endorsementUpdateObj;

	const { fields: fieldConfigurations, exposures: exposureConfigurations } =
		productConfig.policyConfiguration;

	// policy changes
	const changedFieldValues = endorsementUpdateObj.fieldValues
		? { ...endorsementUpdateObj.fieldValues }
		: {};
	const addedFieldGroups = endorsementUpdateObj.addFieldGroups
		? [...endorsementUpdateObj.addFieldGroups]
		: [];
	const removedFieldGroups = endorsementUpdateObj.removeFieldGroups
		? [...endorsementUpdateObj.removeFieldGroups]
		: [];
	const updatedFieldGroups = endorsementUpdateObj.updateFieldGroups
		? [...endorsementUpdateObj.updateFieldGroups]
		: [];

	// exposure changes
	const addedExposures = endorsementUpdateObj.addExposures
		? [...endorsementUpdateObj.addExposures]
		: [];
	const updatedExposures = endorsementUpdateObj.updateExposures
		? [...endorsementUpdateObj.updateExposures]
		: [];
	const endedExposures = endorsementUpdateObj.endExposures
		? [...endorsementUpdateObj.endExposures]
		: [];

	const referenceTimestamp =
		getReferenceTimestampBasedOnState(endorsementResponse);

	const endorsementCharacteristic = policy.characteristics
		.filter((c) =>
			filterForPolicySnapshot(
				c.createdTimestamp,
				c.replacedTimestamp,
				referenceTimestamp,
			),
		)
		.find((char) => {
			return (
				parseInt(char.startTimestamp, 10) <=
					parseInt(endorsementUpdateObj.startTimestamp, 10) &&
				parseInt(char.endTimestamp, 10) >=
					parseInt(endorsementUpdateObj.startTimestamp, 10)
			);
		});

	const hasPaymentPlanPluginEnabled =
		!!productConfig.policyConfiguration?.plugins?.createInstallments?.enabled;
	const paymentPlans = productConfig.policyConfiguration.paymentSchedules;
	const newPaymentPlanDisplayName = newPaymentScheduleName
		? paymentPlans.find((p) => p.name === newPaymentScheduleName).displayName
		: null;
	const hasInputRepriceBehavior = getHasInputRepriceBehavior(
		productConfig,
		endorsementResponse,
	);
	const repriceVal = endorsementUpdateObj.reprice ? 'Yes' : 'No';

	// unsaved changes
	const hasUnsavedChanges = endorsementHasUnsavedChanges(
		endorsementResponse,
		endorsementUpdateObj,
		isDirty,
		dirtyFields,
		productConfig,
		policy,
	);
	const renderUnsavedChangesWarning = () => {
		return (
			<Alert severity='warning'>
				{ENDORSEMENT_REVIEW_CHANGES_UNSAVED_CHANGES_MSG()}
			</Alert>
		);
	};

	return (
		<StyledSectionContainer>
			<StyledTitleContainer>
				<StyledTitle>Review Changes</StyledTitle>
				{hasUnsavedChanges && renderUnsavedChangesWarning()}
			</StyledTitleContainer>
			<hr />
			<StyledRow>
				<FormControl style={{ marginRight: '10px' }}>
					<FieldLabel
						required={false}
						title={`${t('endorsement.singular.upper')} Effective Date`}
					/>
					<TextField
						className={classes.disabledInput}
						disabled
						id='renewalStart'
						value={
							dateConversion
								.convertTimestampToDayJS(startTimestamp, timezone)
								.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]) ||
							''
						}
					/>
				</FormControl>
				{newPolicyEndTimestamp && (
					<FormControl style={{ marginRight: '10px' }}>
						<FieldLabel required={false} title={'New Policy End Date'} />
						<TextField
							className={classes.disabledInput}
							disabled
							id='newPolicyEndTimestamp'
							value={
								dateConversion
									.convertTimestampToDayJS(newPolicyEndTimestamp, timezone)
									.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]) ||
								''
							}
						/>
					</FormControl>
				)}
				{newPaymentScheduleName && hasPaymentPlanPluginEnabled && (
					<FormControl style={{ marginRight: '10px' }}>
						<FieldLabel required={false} title={'New Payment Schedule'} />
						<TextField
							className={classes.disabledInput}
							disabled
							id='newPaymentSchedule'
							value={newPaymentPlanDisplayName || 'N/A'}
						/>
					</FormControl>
				)}
				{hasInputRepriceBehavior && (
					<FormControl style={{ marginRight: '10px' }}>
						<FieldLabel required={false} title={'Reprice'} />
						<TextField
							className={classes.disabledInput}
							disabled
							id='reprice'
							value={repriceVal}
						/>
					</FormControl>
				)}
			</StyledRow>
			<PolicyChanges
				policyCharacteristic={{
					...endorsementCharacteristic,
					fieldGroupsByLocator: getAllCharacteristicsFieldGroupLocators(policy),
					fieldValues: getAllCharacteristicsFieldValues(policy),
				}}
				changedFieldValues={changedFieldValues}
				addedFieldGroups={addedFieldGroups}
				removedFieldGroups={removedFieldGroups}
				updatedFieldGroups={updatedFieldGroups}
				fieldConfigurations={fieldConfigurations}
			/>
			<ExposureChanges
				policyCharacteristic={endorsementCharacteristic}
				exposures={policy.exposures}
				characteristicTimestamp={endorsementUpdateObj.startTimestamp.toString()}
				referenceTimestamp={referenceTimestamp}
				addedExposures={addedExposures}
				updatedExposures={updatedExposures}
				endedExposures={endedExposures}
				exposureConfigurations={exposureConfigurations}
				currency={policy?.currency}
			/>
		</StyledSectionContainer>
	);
};

export default withEndorsementContext<Props>(ReviewChanges);
